import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import LegalLayout from './LegalLayout';
import { legalConfig } from './legalConfig';
import { deleteAccount } from './deleteAccountApi';

export default function DeleteAccountPage() {
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [askingConfirm, setAskingConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (askingConfirm) {
      cancelButtonRef.current?.focus();
    }
  }, [askingConfirm]);

  useEffect(() => {
    if (errors.length > 0) {
      errorRef.current?.focus();
    }
  }, [errors]);

  function onDialogKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      setAskingConfirm(false);
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>('button');
    if (!focusable || focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    // Bấm vào nền mờ làm focus rơi về chính lớp phủ; kéo nó trở lại vào trong
    // thay vì để Tab đi tiếp xuống nội dung trang đang bị che
    if (document.activeElement === dialogRef.current) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    const found: string[] = [];
    if (!userName.trim()) found.push('Vui lòng nhập tài khoản.');
    if (!password) found.push('Vui lòng nhập mật khẩu.');
    if (!confirmed) found.push('Vui lòng xác nhận bạn hiểu tài khoản sẽ bị xoá.');
    setErrors(found);
    if (found.length === 0) {
      setAskingConfirm(true);
    }
  }

  async function onConfirm() {
    setAskingConfirm(false);
    setSubmitting(true);
    const result = await deleteAccount(userName.trim(), password);
    setSubmitting(false);
    if (result.ok) {
      setDone(true);
    } else {
      setErrors([result.message]);
    }
  }

  if (done) {
    return (
      <LegalLayout title="Đã xoá tài khoản">
        <div className="my-5 rounded-md border border-slate-200 border-l-[3px] border-l-blue-700 bg-slate-50 p-4 dark:border-slate-700 dark:border-l-blue-400 dark:bg-slate-800">
          <p>
            <strong>Tài khoản của bạn đã được xoá.</strong>
          </p>
        </div>

        <p className="mb-3">Kể từ bây giờ:</p>
        <ul className="mb-3 list-disc space-y-1.5 pl-6">
          <li>Bạn không đăng nhập lại được vào tài khoản này trên bất kỳ thiết bị nào.</li>
          <li>Mọi thiết bị đang đăng ký với tài khoản đã bị thu hồi.</li>
          <li>Quyền truy cập vào các sách đã kích hoạt đã chấm dứt.</li>
        </ul>

        <p className="mb-3">
          Nếu bạn đã cài ứng dụng EBD Learning trên máy nào, hãy mở ứng dụng và đăng xuất, hoặc gỡ cài
          đặt, để xoá nốt dữ liệu còn lưu trên máy đó.
        </p>

        <p className="mb-3">
          Phần dữ liệu được giữ lại và thời gian lưu trữ được nêu tại{' '}
          <Link className="text-blue-700 dark:text-blue-400" to="/privacy">
            Chính sách bảo mật
          </Link>
          . Nếu bạn cần hỗ trợ thêm, liên hệ{' '}
          <a className="text-blue-700 dark:text-blue-400" href={`mailto:${legalConfig.email}`}>
            {legalConfig.email}
          </a>
          .
        </p>
      </LegalLayout>
    );
  }

  return (
    <LegalLayout title="Xoá tài khoản">
      <p className="mb-3">
        Bạn có thể xoá tài khoản <strong>EBD Learning</strong> ngay tại trang này mà không cần cài
        ứng dụng. Nếu đang dùng ứng dụng, bạn cũng có thể xoá trực tiếp trong đó.
      </p>

      <h2 className="mt-9 mb-3 border-b border-slate-200 pb-2 text-xl font-semibold dark:border-slate-700">
        1. Xoá ngay tại đây
      </h2>
      <p className="mb-3">
        Nhập tài khoản và mật khẩu bạn dùng để đăng nhập. Chúng tôi cần mật khẩu để chắc chắn chính
        bạn là người yêu cầu xoá.
      </p>

      <div className="my-6 rounded-lg border border-slate-200 bg-slate-50 p-5 dark:border-slate-700 dark:bg-slate-800">
        {errors.length > 0 && (
          <div
            ref={errorRef}
            role="alert"
            tabIndex={-1}
            className="mb-5 rounded-md border border-l-[3px] border-red-700 p-3"
          >
            <ul className="list-disc pl-5">
              {errors.map((message) => (
                <li key={message} className="text-red-700 dark:text-red-400">
                  {message}
                </li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={onSubmit} noValidate>
          <div className="mb-4">
            <label htmlFor="user_name" className="mb-1.5 block font-semibold">
              Tài khoản
            </label>
            <input
              id="user_name"
              type="text"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              className="w-full rounded-md border border-slate-300 bg-white p-2.5 text-base dark:border-slate-600 dark:bg-slate-900"
            />
          </div>

          <div className="mb-4">
            <label htmlFor="password" className="mb-1.5 block font-semibold">
              Mật khẩu
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full rounded-md border border-slate-300 bg-white p-2.5 text-base dark:border-slate-600 dark:bg-slate-900"
            />
          </div>

          <div className="mb-4 flex items-start gap-2.5">
            <input
              id="confirm"
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-1.5"
            />
            <label htmlFor="confirm">
              Tôi hiểu tài khoản sẽ bị xoá và tôi sẽ không đăng nhập lại được, kể cả với những cuốn
              sách đã kích hoạt.
            </label>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-red-700 px-5 py-2.5 font-semibold text-white hover:bg-red-800 disabled:opacity-60"
          >
            {submitting ? 'Đang xoá...' : 'Xoá tài khoản của tôi'}
          </button>
        </form>
      </div>

      {askingConfirm && (
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-title"
          // tabIndex để lớp phủ nhận được focus khi người dùng bấm vào nền mờ —
          // thiếu nó thì focus rơi về body và Escape không còn tới được handler
          tabIndex={-1}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onKeyDown={onDialogKeyDown}
        >
          <div className="w-full max-w-md rounded-lg bg-white p-6 dark:bg-slate-800">
            <h3 id="confirm-title" className="mb-2 text-lg font-semibold">
              Xoá tài khoản vĩnh viễn?
            </h3>
            <p className="mb-5">
              Sau khi xoá, bạn không đăng nhập lại được và không lấy lại được sách đã kích hoạt.
            </p>
            <div className="flex justify-end gap-3">
              <button
                ref={cancelButtonRef}
                type="button"
                onClick={() => setAskingConfirm(false)}
                className="rounded-md border border-slate-300 px-4 py-2 dark:border-slate-600"
              >
                Huỷ
              </button>
              <button
                type="button"
                onClick={onConfirm}
                className="rounded-md bg-red-700 px-4 py-2 font-semibold text-white hover:bg-red-800"
              >
                Xoá vĩnh viễn
              </button>
            </div>
          </div>
        </div>
      )}

      <h2 className="mt-9 mb-3 border-b border-slate-200 pb-2 text-xl font-semibold dark:border-slate-700">
        2. Hoặc xoá trong ứng dụng
      </h2>
      <p className="mb-3">Mở ứng dụng EBD Learning và làm theo các bước:</p>
      <ul className="mb-3 list-disc space-y-1.5 pl-6">
        <li>Vào mục <strong>Hồ sơ</strong>.</li>
        <li>Chọn <strong>Xoá tài khoản</strong>.</li>
        <li>Đọc phần cảnh báo, nhập mật khẩu hiện tại rồi xác nhận.</li>
      </ul>

      <h2 className="mt-9 mb-3 border-b border-slate-200 pb-2 text-xl font-semibold dark:border-slate-700">
        3. Điều gì xảy ra khi bạn xoá
      </h2>
      <p className="mb-3">Ngay khi yêu cầu được xử lý:</p>
      <ul className="mb-3 list-disc space-y-1.5 pl-6">
        <li>Tài khoản bị vô hiệu hoá và không đăng nhập lại được trên bất kỳ thiết bị nào.</li>
        <li>Mọi thiết bị đang đăng ký với tài khoản bị thu hồi, các phiên đang mở dừng hoạt động.</li>
        <li>
          Quyền truy cập vào toàn bộ sách đã kích hoạt chấm dứt. Mã kích hoạt đã dùng không dùng lại
          được cho tài khoản khác.
        </li>
        <li>
          Nếu bạn xoá từ trong ứng dụng, dữ liệu đã tải về máy đó — sách, tiến độ học, thông tin đăng
          nhập — được xoá khỏi thiết bị.
        </li>
      </ul>

      <div className="my-5 rounded-md border border-slate-200 border-l-[3px] border-l-blue-700 bg-slate-50 p-4 dark:border-slate-700 dark:border-l-blue-400 dark:bg-slate-800">
        <p>
          <strong>Xoá tài khoản là thao tác không hoàn tác được.</strong> Chúng tôi không khôi phục
          lại được tài khoản đã xoá, và bạn sẽ phải mua mã kích hoạt mới nếu muốn dùng lại nội dung số
          của những cuốn sách đó.
        </p>
      </div>

      <h2 className="mt-9 mb-3 border-b border-slate-200 pb-2 text-xl font-semibold dark:border-slate-700">
        4. Dữ liệu được giữ lại
      </h2>
      <p className="mb-3">
        Một phần dữ liệu vẫn được lưu sau khi tài khoản bị xoá, phục vụ nghĩa vụ lưu trữ, đối soát bản
        quyền nội dung và giải quyết khiếu nại. Thời gian và phạm vi lưu trữ được nêu tại mục{' '}
        <Link className="text-blue-700 dark:text-blue-400" to="/privacy">
          Thời gian lưu trữ trong Chính sách bảo mật
        </Link>
        .
      </p>
      <p className="mb-3">
        Với tài khoản học sinh do nhà trường tạo, hồ sơ do nhà trường quản lý có thể vẫn được nhà
        trường lưu giữ độc lập với tài khoản EBD Learning của bạn.
      </p>

      <h2 className="mt-9 mb-3 border-b border-slate-200 pb-2 text-xl font-semibold dark:border-slate-700">
        5. Cần hỗ trợ
      </h2>
      <p className="mb-3">
        Nếu bạn không nhớ mật khẩu hoặc không đăng nhập được, gửi yêu cầu xoá tài khoản tới{' '}
        <a className="text-blue-700 dark:text-blue-400" href={`mailto:${legalConfig.email}`}>
          {legalConfig.email}
        </a>
        , kèm tài khoản hoặc địa chỉ email đã đăng ký. Chúng tôi phản hồi trong vòng 15 ngày làm việc
        và có thể cần xác minh danh tính trước khi xử lý. Với tài khoản do nhà trường cấp, chúng tôi
        có thể cần xác nhận từ nhà trường.
      </p>
      <p className="mb-3">
        {legalConfig.company}
        <br />
        {legalConfig.address}
        <br />
        Điện thoại: {legalConfig.phone}
      </p>
    </LegalLayout>
  );
}
